<template>
  <div id="ImageUpload">
    <p
      v-if="question.sub_text_html"
      v-html="decodeData(question.sub_text_html)"
      class="sub-text"
    ></p>

    <label for="image-file" class="label">
      <span>Upload files</span>
      <i class="fa fa-plus" aria-hidden="true"></i>
    </label>
    <b-spinner v-if="isImageUploading" class="mt-4"></b-spinner>
    <input
      id="image-file"
      type="file"
      class="input"
      :accept="fileExtensions.join(',')"
      @change="handleFiles"
    />
    <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

    <div class="preview-container" v-if="previewImages && previewImages.length">
      <div v-for="(image, index) in previewImages" :key="index">
        <div
          v-if="image.question_id === question.question_id"
          class="image-preview"
        >
          <img
            v-if="image.isImage"
            class="image"
            :src="image.src"
            :alt="'Preview ' + (index + 1)"
          />
          <div v-else class="file-preview">
            <i class="fa fa-file" aria-hidden="true"></i>
            <span class="file-name">{{ image.displayName || image.name }}</span>
          </div>
          <button class="remove-button" @click="removeImage(index, image)">
            Remove
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ImageUpload",
  props: ["setAnswer", "answer", "question", "bookingJourneyId", "serviceType", "userId", "apiQuestionnaire"],
  data() {
    return {
      errorMessage: "",
      isImageUploading: false,
      isImageRemoving: false,
      previewImages: [],
      fileNames: []
    };
  },
  computed: {
    validation() {
      return this.question?.validation;
    },
    fileExtensions() {
      return (
        this.validation?.file_extensions.map(ext => `.${ext.toLowerCase()}`) ||
        []
      );
    },
    sizeLimitNumber() {
      return this.validation.max_file_size_mb;
    },
    previousImagesNames() {
      return this.question?.previous_answer || [];
    }
  },
  mounted() {
    this.setAnswer(this.question?.previous_answer || null);
    this.loadPreviousImages();
  },
  methods: {
    decodeData(data) {
      try {
        return atob(data);
      } catch (error) {
        return data;
      }
    },
    encodeData(data) {
      try {
        const jsonString = JSON.stringify(data);
        const base64String = btoa(unescape(encodeURIComponent(jsonString)));
        return base64String;
      } catch (error) {
        console.error("Error encoding to Base64:", error);
        return null;
      }
    },
    fetchUploadedImage(fileName) {
      const payload = {
        user_id: this.userId,
        file_name: fileName
      };
      const encodedData = this.encodeData(payload);

      return this.apiQuestionnaire.getFileViewUrl(encodedData);
    },
    async loadPreviousImages() {
      if (!this.previousImagesNames.length) return;

      for (const fileName of this.previousImagesNames) {
        const url = await this.fetchUploadedImage(fileName);
        if (url) {
          const uuidMatch = fileName.match(/-([a-f0-9]+)\.\w+$/i);
          const uuid = uuidMatch ? uuidMatch[1] : null;

          this.previewImages.push({
            src: url,
            name: fileName,
            displayName: fileName,
            isImage: this.isImageFile(fileName),
            uuid,
            uploadedUrl: url,
            question_id: this.question.question_id
          });
        }
      }

      this.fileNames = this.previousImagesNames;
      this.setAnswer(this.fileNames);
    },
    async handleFiles(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.errorMessage = "";

      if (file.size === 0) {
        this.errorMessage = `File "${file.name}" is empty and cannot be uploaded.`;
        event.target.value = "";
        return;
      }

      const maxFiles = this.validation?.max_number_of_files || Infinity;
      if (this.previewImages.length >= maxFiles) {
        this.errorMessage = `You can upload a maximum of ${maxFiles} files.`;
        event.target.value = "";
        return;
      }

      const allowedExtensions = this.validation.file_extensions.map(ext =>
        ext.toLowerCase()
      );
      const fileExtension = file.name
        .split(".")
        .pop()
        .toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        this.errorMessage = `File type not supported. Accepted file types: ${this.fileExtensions.join(", ")}.`;
        return;
      }

      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > this.sizeLimitNumber) {
        this.errorMessage = `File "${file.name}" is too large (max ${this.sizeLimitNumber}MB).`;
        return;
      }

      const formData = new FormData();
      formData.append("images[]", file);

      const fields = {
        user_id: this.userId,
        booking_journey_id: this.bookingJourneyId,
        question_id: this.question.question_id,
        questionnaire: this.serviceType
      };
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const fileInfo = await this.uploadSingleFile(formData);

      if (fileInfo) {
        const isImage = this.isImageFile(file.name);

        const addPreview = src => {
          this.previewImages.push({
            src,
            name: fileInfo.filename,
            displayName: file.name,
            isImage,
            size: `${sizeInMB.toFixed(2)} MB`,
            uuid: fileInfo.uuid,
            uploadedUrl: fileInfo.url,
            question_id: this.question.question_id
          });

          this.fileNames = this.previewImages.map(f => f.name);
          this.setAnswer(this.fileNames);
        };

        if (isImage) {
          const reader = new FileReader();
          reader.onload = e => addPreview(e.target.result);
          reader.readAsDataURL(file);
        } else {
          addPreview(null);
        }
      }

      event.target.value = "";
    },
    async uploadSingleFile(formData) {
      try {
        this.isImageUploading = true;
        this.$emit("uploading", true);
        const res = await this.apiQuestionnaire.uploadFile(formData);

        if (res?.data && res.data.success) {
          const fileInfoList = this.extractFileInfo(res.data.files);
          const fileInfo = fileInfoList[0];

          return fileInfo;
        } else {
          this.errorMessage = "Upload failed. Please try again or choose a different file.";
          return null;
        }
      } catch (error) {
        this.errorMessage =
          "Upload failed. Please try again or choose a different file.";
        return null;
      } finally {
        this.isImageUploading = false;
        this.$emit("uploading", false);
      }
    },
    async removeImage(index, imageData) {
      if (this.isImageRemoving) {
        return;
      }
      try {
        this.errorMessage = "";
        this.isImageRemoving = true;
        const payload = {
          user_id: this.userId,
          booking_journey_id: this.bookingJourneyId,
          questionnaire: this.serviceType,
          question_id: this.question.question_id,
          uuid: imageData.uuid
        };
        const res = await this.apiQuestionnaire.deleteFile(payload);
        if (res.data.success) {
          this.previewImages.splice(index, 1);

          this.fileNames = this.previewImages.map(file => {
            return file.name;
          });

          this.setAnswer(this.fileNames);
        } else {
          this.errorMessage = "Failed to remove the file."
        }
      } catch (err) {
        this.errorMessage = "Failed to remove the file."
        console.error(err);
      } finally {
        this.isImageRemoving = false;
      }
    },
    extractFileInfo(files) {
      const filenames = Object.keys(files);

      return filenames.map(filename => {
        const uuidMatch = filename.match(/-([a-f0-9]+)\.\w+$/i);
        const uuid = uuidMatch ? uuidMatch[1] : null;

        return {
          filename,
          uuid,
          url: files[filename].aws_response
        };
      });
    },
    isImageFile(name) {
      const extension = (name || "")
        .split(".")
        .pop()
        .toLowerCase();
      return ["png", "jpg", "jpeg"].includes(extension);
    }
  }
};
</script>

<style lang="less">
#ImageUpload {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .label {
    cursor: pointer;
    display: flex;
    gap: 8px;
    align-items: center;
    background-color: rgba(30, 30, 34, 0.05);
    padding: 16px;
    border-radius: 5px;
    color: #1e1e22;
    font-size: 1.4rem;
    font-family: var(--font-bold, "DMSans Bold");

    .fa {
      font-size: 2rem;
    }
  }

  .input {
    visibility: hidden;
  }

  .preview-container {
    display: flex;
    flex-direction: column;
    margin-top: 12px;
  }

  .image-preview {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 20px;
  }

  .image {
    width: 138px;
    height: 138px;
    object-fit: contain;
    border-radius: 5px;
    background-color: rgba(30, 30, 34, 0.05);
  }

  .file-preview {
    width: 138px;
    height: 138px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    background-color: rgba(30, 30, 34, 0.05);
    border-radius: 5px;

    .fa {
      font-size: 2.4rem;
      color: #1e1e22;
    }

    .file-name {
      font-size: 1.2rem;
      line-height: 1.3;
      color: #1e1e22;
      text-align: center;
      width: 100%;
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .remove-button {
    font-size: 1.6rem;
    font-family: var(--font-bold, "DMSans Bold");
    border-radius: 12px;
    border: 1px solid #fd3d3d;
    padding: 16px 20px;
    background-color: #fff;
    color: #fd3d3d;
    margin: 0;
  }

  .error-message {
    color: #fd3d3d;
    font-size: 1.6rem;
    font-family: var(--font-regular, "DMSans");
  }

  .sub-text {
    width: 100%;
    h3 {
      font-size: 2.2rem;
      letter-spacing: 0;
      color: #1e1e22;
      font-family: var(--font-bold, "DMSans Bold");
      padding: 0;
      margin: 0 0 32px 0;
    }

    p {
      color: #1e1e22;
      font-size: 1.8rem;
      margin-bottom: 24px;
    }
  }
}
</style>
